import { useState, useCallback, useRef } from 'react';
import { isAlreadyExecutingError } from '@/components/execution/execution-request-error';

interface ExecuteResult {
  guardianTransactionId: string;
}

interface UseExecutionStatusModalStateOptions<TContext = undefined> {
  /**
   * Controls whether modal copy refers to proposal or transaction execution.
   */
  executionTarget: 'proposal' | 'transaction';
  /**
   * Callback when execution completes successfully.
   * Called after the modal reports success with the context passed to startExecution.
   */
  onSuccess?: (context: TContext | undefined) => void | Promise<unknown>;

  /**
   * Callback when execution fails.
   * Called when the modal reports an error.
   */
  onError?: (errorMessage: string | null) => void;

  /**
   * Callback to refresh data after modal closes.
   * Called when the modal is closed (regardless of success/error).
   * Accepts SWR's KeyedMutator return type (Promise<T | undefined>).
   */
  onClose?: () => void | Promise<unknown>;

  /** Refreshes the signable after another user wins the execution race. */
  onAlreadyExecuting?: () => void | Promise<unknown>;
}

/**
 * Hook that bundles all state and handlers needed for ExecutionStatusModal.
 *
 * This reduces boilerplate when using ExecutionStatusModal by providing:
 * - All required state variables (guardianTransactionId, isOpen, immediateError)
 * - A startExecution function to kick off async execution
 * - All callback handlers for the modal (onSuccess, onError, onClose)
 * - Modal props for execution status and neutral already-executing conflicts
 *
 * @example
 * ```tsx
 * const { modalProps, startExecution } = useExecutionStatusModalState({
 *   onSuccess: () => {
 *     onApplied();
 *     onOpenChange(false);
 *   },
 *   onClose: refreshProposals,
 * });
 *
 * const handleExecute = () => {
 *   startExecution(() => executeProposal({ proposalId }));
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleExecute}>Execute</Button>
 *     <ExecutionStatusModal {...modalProps} />
 *   </>
 * );
 * ```
 */
export function useExecutionStatusModalState<TContext = undefined>(
  options: UseExecutionStatusModalStateOptions<TContext>
) {
  const { executionTarget, onSuccess, onError, onClose, onAlreadyExecuting } =
    options;

  const [guardianTransactionId, setGuardianTransactionId] = useState<
    string | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isAlreadyExecutingOpen, setIsAlreadyExecutingOpen] = useState(false);
  const [immediateError, setImmediateError] = useState<string | null>(null);
  // Captured synchronously in startExecution so onSuccess does not depend on React state.
  const pendingContextRef = useRef<TContext | undefined>(undefined);

  /**
   * Start an async execution. Opens the modal immediately and then
   * calls the provided execute function.
   *
   * Both APPROVE and REJECT actions create a GuardianTransaction and are
   * processed via the async worker. The modal polls the transaction status
   * and triggers callbacks on completion.
   *
   * @param executeFn - Async function that returns { guardianTransactionId }
   * @param context - Optional value forwarded to onSuccess when execution completes
   */
  const startExecution = useCallback(
    async (executeFn: () => Promise<ExecuteResult>, context?: TContext) => {
      pendingContextRef.current = context;
      // Reset state and open modal immediately
      setGuardianTransactionId(null);
      setImmediateError(null);
      setIsAlreadyExecutingOpen(false);
      setIsOpen(true);

      try {
        const result = await executeFn();
        // Update modal with the guardianTransactionId to start polling
        setGuardianTransactionId(result.guardianTransactionId);
      } catch (error) {
        // Case: another user started this signable first. Replace the failure
        // modal with the neutral concurrency dialog and refresh best-effort.
        if (isAlreadyExecutingError(error)) {
          setIsOpen(false);
          setGuardianTransactionId(null);
          setImmediateError(null);
          setIsAlreadyExecutingOpen(true);
          pendingContextRef.current = undefined;
          try {
            await onAlreadyExecuting?.();
          } catch (refreshError: unknown) {
            console.error(
              'Failed to refresh after an already-executing response',
              refreshError
            );
          }
          return;
        }

        console.error('Failed to execute proposal', error);
        // Show error in the modal that's already open
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to execute proposal';
        setImmediateError(errorMessage);
        pendingContextRef.current = undefined;
      }
    },
    [onAlreadyExecuting]
  );

  /**
   * Handler for successful execution - called by ExecutionStatusModal.
   */
  const handleSuccess = useCallback(async () => {
    const context = pendingContextRef.current;
    pendingContextRef.current = undefined;
    await onSuccess?.(context);
    await onClose?.();
    // Reset execution modal state
    setIsOpen(false);
    setGuardianTransactionId(null);
  }, [onSuccess, onClose]);

  /**
   * Handler for execution error - called by ExecutionStatusModal.
   */
  const handleError = useCallback(
    (errorMessage: string | null) => {
      console.error('Proposal execution failed:', errorMessage);
      // Modal stays open to show error, user can close it manually
      onError?.(errorMessage);
    },
    [onError]
  );

  /**
   * Handler for modal close - called by ExecutionStatusModal.
   */
  const handleClose = useCallback(async () => {
    setIsOpen(false);
    setGuardianTransactionId(null);
    setImmediateError(null);
    setIsAlreadyExecutingOpen(false);
    pendingContextRef.current = undefined;
    // Refresh data in case state changed
    await onClose?.();
  }, [onClose]);

  /**
   * Reset all state (useful if you need to reset manually)
   */
  const reset = useCallback(() => {
    setGuardianTransactionId(null);
    setIsOpen(false);
    setImmediateError(null);
    setIsAlreadyExecutingOpen(false);
  }, []);

  return {
    // Individual state values (in case you need them)
    guardianTransactionId,
    isOpen,
    immediateError,

    // Functions
    startExecution,
    reset,

    // Props object ready to spread onto ExecutionStatusModal
    modalProps: {
      executionTarget,
      open: isOpen,
      guardianTransactionId,
      immediateError,
      onSuccess: handleSuccess,
      onError: handleError,
      onClose: handleClose,
    },
    alreadyExecutingDialogProps: {
      executionTarget,
      open: isAlreadyExecutingOpen,
      onOpenChange: setIsAlreadyExecutingOpen,
    },
  };
}
